import { useQuery } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch } from '../lib/api'
import type { ProfileData } from './use-profile'

async function profileRequestErrorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '')
  const trimmed = text.trim()
  if (!trimmed) {
    return `Request failed (${response.status})`
  }
  try {
    const data = JSON.parse(trimmed) as Record<string, unknown>
    if (typeof data.error === 'string' && data.error) return data.error
  } catch {
    /* not JSON */
  }
  return trimmed.length > 300 ? `${trimmed.slice(0, 297)}…` : trimmed
}

export function useProfileById(profileId: string | null) {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ['profile', profileId],
    queryFn: async (): Promise<ProfileData> => {
      if (!token) throw new Error('Not authenticated')
      if (!profileId) throw new Error('Profile ID required')

      const response = await authorizedFetch(`/api/profiles/${profileId}`)
      if (!response.ok) {
        throw new Error(await profileRequestErrorMessage(response))
      }
      return response.json()
    },
    enabled: !!token && !!profileId,
    staleTime: 60 * 1000,
  })

  return {
    profile: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
